import {
  computePartSizes,
  EDGE_IDS,
  PROFILE_FINISHED,
  PROFILE_SUBSTRATE,
  type EdgeDecision,
  type EdgeId,
  type EvaluatedProject,
  type MeasurementProfile,
  type ProjectDocument,
  type Um,
} from '@scs/domain-engine';
import { findDecor } from '@scs/material-model';

export const MEASUREMENT_PROFILES: Record<string, MeasurementProfile> = {
  [PROFILE_FINISHED.id]: PROFILE_FINISHED,
  [PROFILE_SUBSTRATE.id]: PROFILE_SUBSTRATE,
};

export interface CutListEdge {
  decision: EdgeDecision['decision'];
  thickness: Um;
  productId: string | null;
  /** Supplier/manufacturer code of the chosen band, if known. */
  code: string;
}

/** Catalog lookups used to print codes; demo decors are the fallback. */
export interface CatalogLookup {
  decor(id: string): { code: string; name: string } | undefined;
  edgeBand(id: string): { code: string; name: string; thickness: Um; width: Um } | undefined;
}

export const DEMO_CATALOG: CatalogLookup = {
  decor: (id) => findDecor(id),
  edgeBand: () => undefined,
};

export interface EdgeBandTotal {
  /** productId or `t:<thickness>` when no product was chosen. */
  key: string;
  productId: string | null;
  code: string;
  name: string;
  thickness: Um;
  width: Um;
  edges: number;
  /** Sum of finished edge lengths. */
  net: Um;
  /** Net + overhang per edge, plus waste percent (profile). */
  gross: Um;
}

export interface CutListRow {
  /** Grouping key: identical rows differ in nothing that matters for manufacturing. */
  signature: string;
  quantity: number;
  partIds: string[];
  cabinetNames: string[];
  label: string;
  materialId: string;
  materialCode: string;
  materialName: string;
  thickness: Um;
  finished: { length: Um; width: Um };
  order: { length: Um; width: Um };
  grain: 'length' | 'width' | 'none';
  edges: Record<EdgeId, CutListEdge>;
  /** Net banding length per edge thickness (µm), for this row's total quantity. */
  edgeLength: Record<string, Um>;
  unresolvedEdges: number;
  notes: string[];
  /** Irregular (CNC) outline in mm-free µm rectangles relative to the part's min corner. */
  cnc?: { outline: { x0: Um; y0: Um; x1: Um; y1: Um }[]; polygon?: [Um, Um][]; note?: string };
}

export interface CutList {
  profile: MeasurementProfile;
  rows: CutListRow[];
  /** Irregular parts for CNC machining: one row per part, sizes are the bounding box. */
  cnc: CutListRow[];
  totals: {
    parts: number;
    edgeLengthByThickness: Record<string, Um>;
    edgeBands: EdgeBandTotal[];
    /** Total length of cuts to be sealed (wet areas), µm. */
    sealLength: Um;
    unresolvedEdges: number;
  };
}

/** Builds the grouped cut list from an evaluated project (server is authoritative for production). */
export function buildCutList(
  doc: ProjectDocument,
  evaluated: EvaluatedProject,
  opts: { catalog?: CatalogLookup; profile?: MeasurementProfile } = {},
): CutList {
  const profile = opts.profile ?? MEASUREMENT_PROFILES[doc.settings.measurementProfileId] ?? PROFILE_FINISHED;
  const catalog = opts.catalog ?? DEMO_CATALOG;
  const groups = new Map<string, CutListRow>();
  const bandTotals = new Map<string, EdgeBandTotal>();
  let sealLength = 0;
  for (const cab of evaluated.cabinets) {
    for (const part of cab.result.parts) {
      if (!part.inCutList) continue;
      const decisions = cab.edges.find((e) => e.partId === part.id)?.decisions;
      if (!decisions) continue;
      const materialId = cab.instance.materials[part.materialSlot] ?? doc.materials[part.materialSlot];
      const decor = catalog.decor(materialId);
      const sizes = computePartSizes({ length: part.length, width: part.width }, decisions, profile);
      const edges = Object.fromEntries(
        EDGE_IDS.map((e) => [
          e,
          {
            decision: decisions[e].decision,
            thickness: decisions[e].band?.thickness ?? 0,
            productId: decisions[e].band?.productId ?? null,
            code: decisions[e].band?.productId ? (catalog.edgeBand(decisions[e].band!.productId!)?.code ?? '?') : '',
          },
        ]),
      ) as Record<EdgeId, CutListEdge>;
      const signature = JSON.stringify([
        part.cnc ? `cnc:${part.id}` : '',
        part.cutNote ?? '',
        materialId,
        part.thickness,
        sizes.finished.length,
        sizes.finished.width,
        part.grain,
        EDGE_IDS.map((e) => [edges[e].decision, edges[e].thickness, edges[e].productId]),
        // Machining operations will join the signature in Phase 8.
      ]);
      let row = groups.get(signature);
      if (!row) {
        row = {
          signature,
          quantity: 0,
          partIds: [],
          cabinetNames: [],
          label: part.label,
          materialId,
          materialCode: decor?.code ?? '',
          materialName: decor?.name ?? `Nepoznat materijal (${materialId})`,
          thickness: part.thickness,
          finished: sizes.finished,
          order: sizes.order,
          grain: part.grain,
          edges,
          edgeLength: {},
          unresolvedEdges: 0,
          notes: [],
        };
        if (!decor) row.notes.push('Materijal nije u katalogu.');
        if (part.generic) row.notes.push('Generički dio (ručni model okova).');
        if (part.cnc) {
          row.cnc = part.cnc;
          row.notes.push('Nepravilan oblik — CNC obrada; mjere su gabarit, oblik prema obrisu.');
          if (part.cnc.note) row.notes.push(part.cnc.note);
        }
        if (part.cutNote) row.notes.push(part.cutNote);
        groups.set(signature, row);
      }
      row.quantity++;
      row.partIds.push(part.id);
      if (!row.label.split(' / ').includes(part.label)) row.label = `${row.label} / ${part.label}`;
      if (!row.cabinetNames.includes(cab.instance.name)) row.cabinetNames.push(cab.instance.name);
      for (const e of EDGE_IDS) {
        const d = decisions[e];
        if (d.decision === 'unresolved') row.unresolvedEdges++;
        if (d.decision === 'seal') sealLength += part.edges[e].length;
        if (d.decision === 'band' && d.band) {
          const k = String(d.band.thickness);
          row.edgeLength[k] = (row.edgeLength[k] ?? 0) + part.edges[e].length;
          const bk = d.band.productId ?? `t:${d.band.thickness}:${d.band.width}`;
          const product = d.band.productId ? catalog.edgeBand(d.band.productId) : undefined;
          const t = bandTotals.get(bk) ?? {
            key: bk,
            productId: d.band.productId,
            code: product?.code ?? '',
            name: product?.name ?? (d.band.productId ? 'Nepoznata traka' : 'Traka nije izabrana'),
            thickness: d.band.thickness,
            width: d.band.width,
            edges: 0,
            net: 0,
            gross: 0,
          };
          t.edges++;
          t.net += part.edges[e].length;
          bandTotals.set(bk, t);
        }
      }
    }
  }
  const all = [...groups.values()];
  const rows = all.filter((r) => !r.cnc);
  const cnc = all.filter((r) => r.cnc);
  const edgeLengthByThickness: Record<string, Um> = {};
  for (const r of rows) for (const [k, v] of Object.entries(r.edgeLength)) edgeLengthByThickness[k] = (edgeLengthByThickness[k] ?? 0) + v;
  const edgeBands = [...bandTotals.values()].map((t) => ({
    ...t,
    gross: Math.round((t.net + t.edges * profile.edgeOverhangPerEdge) * (1 + profile.edgeWastePercent / 100)),
  }));
  return {
    profile,
    rows,
    cnc,
    totals: {
      parts: all.reduce((s, r) => s + r.quantity, 0),
      edgeLengthByThickness,
      edgeBands,
      sealLength,
      unresolvedEdges: all.reduce((s, r) => s + r.unresolvedEdges, 0),
    },
  };
}

export interface CsvOptions {
  delimiter: ';' | ',' | '\t';
  decimal: ',' | '.';
}

/** Neutralises spreadsheet formula injection (=, +, -, @, tab, CR) in text cells. */
export function safeText(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

function csvCell(v: string, delimiter: string): string {
  return /["\r\n]/.test(v) || v.includes(delimiter) ? `"${v.replace(/"/g, '""')}"` : v;
}

function metres(v: Um, decimal: string): string {
  const s = (v / 1_000_000).toFixed(2);
  return decimal === ',' ? s.replace('.', ',') : s;
}

function num(v: Um, decimal: string): string {
  const s = (v / 1000).toFixed(1).replace(/\.0$/, '');
  return decimal === ',' ? s.replace('.', ',') : s;
}

const EDGE_LABEL = (e: CutListEdge, decimal: string) =>
  e.decision === 'band'
    ? `${num(e.thickness, decimal)}${e.code ? ` ${e.code}` : ''}`
    : e.decision === 'unresolved'
      ? '?'
      : e.decision === 'seal'
        ? 'Z'
        : e.decision === 'factory'
          ? 'F'
          : '';

export function cutListToCsv(
  list: CutList,
  meta: { projectName: string; revision: number; date: string },
  opts: CsvOptions = { delimiter: ';', decimal: ',' },
): string {
  const d = opts.delimiter;
  const lines: string[][] = [
    ['Projekat', safeText(meta.projectName), 'Revizija', String(meta.revision), 'Datum', meta.date],
    ['Profil', safeText(list.profile.label), 'Jedinice', 'mm', 'Status provjere', list.rows.some((r) => r.unresolvedEdges) ? 'IMA NERIJEŠENIH IVICA' : 'OK'],
    [],
    [
      'Rb',
      'Element',
      'Dio',
      'Šifra materijala',
      'Materijal',
      'Debljina',
      'Kom',
      'Dužina (narudžba)',
      'Širina (narudžba)',
      'Dužina gotova',
      'Širina gotova',
      'God',
      'Kant L1',
      'Kant L2',
      'Kant W1',
      'Kant W2',
      'Napomena',
    ],
  ];
  list.rows.forEach((r, i) => {
    lines.push([
      String(i + 1),
      safeText(r.cabinetNames.join(', ')),
      safeText(r.label),
      safeText(r.materialCode),
      safeText(r.materialName),
      num(r.thickness, opts.decimal),
      String(r.quantity),
      num(r.order.length, opts.decimal),
      num(r.order.width, opts.decimal),
      num(r.finished.length, opts.decimal),
      num(r.finished.width, opts.decimal),
      r.grain === 'length' ? 'po dužini' : r.grain === 'width' ? 'po širini' : 'bez',
      EDGE_LABEL(r.edges.L1, opts.decimal),
      EDGE_LABEL(r.edges.L2, opts.decimal),
      EDGE_LABEL(r.edges.W1, opts.decimal),
      EDGE_LABEL(r.edges.W2, opts.decimal),
      safeText(r.notes.join(' ')),
    ]);
  });
  if (list.cnc.length) {
    lines.push([]);
    lines.push(['CNC obrada (nepravilni oblici)', 'Mjere su gabarit; obris: pravougaonici x0-x1 / y0-y1 od donjeg lijevog ugla, mm']);
    list.cnc.forEach((r, i) => {
      const outline = r.cnc!.polygon
        ? `poligon (dužina, širina): ${r.cnc!.polygon.map(([u, v]) => `(${num(u, opts.decimal)}, ${num(v, opts.decimal)})`).join(' ')}`
        : r.cnc!.outline.map((o) => `${num(o.x0, opts.decimal)}-${num(o.x1, opts.decimal)} / ${num(o.y0, opts.decimal)}-${num(o.y1, opts.decimal)}`).join(' + ');
      lines.push([
        `C${i + 1}`,
        safeText(r.cabinetNames.join(', ')),
        safeText(r.label),
        safeText(r.materialCode),
        safeText(r.materialName),
        num(r.thickness, opts.decimal),
        String(r.quantity),
        num(r.order.length, opts.decimal),
        num(r.order.width, opts.decimal),
        num(r.finished.length, opts.decimal),
        num(r.finished.width, opts.decimal),
        r.grain === 'length' ? 'po dužini' : r.grain === 'width' ? 'po širini' : 'bez',
        '',
        '',
        '',
        '',
        safeText(`Obris: ${outline}. ${r.notes.join(' ')}`),
      ]);
    });
  }
  lines.push([]);
  lines.push(['Legenda ivica', 'L1/L2 = ivice duž dužine (na širini 0 / max); W1/W2 = ivice duž širine (na dužini 0 / max); Z = zaptivanje reza; F = fabrička ivica; ? = neriješeno']);
  lines.push([]);
  lines.push(['Kant trake', 'Šifra', 'Naziv', 'Debljina', 'Širina', 'Ivica', 'Neto m', 'Bruto m (prepust+otpad)']);
  for (const t of list.totals.edgeBands) {
    lines.push(['', safeText(t.code), safeText(t.name), num(t.thickness, opts.decimal), num(t.width, opts.decimal), String(t.edges), metres(t.net, opts.decimal), metres(t.gross, opts.decimal)]);
  }
  if (list.totals.sealLength) lines.push(['Zaptivanje rezova', '', '', '', '', '', metres(list.totals.sealLength, opts.decimal), '']);
  // UTF-8 BOM so spreadsheet apps detect diacritics correctly.
  return '﻿' + lines.map((l) => l.map((c) => csvCell(c, d)).join(d)).join('\r\n') + '\r\n';
}
